import {
  Button,
  CascaderField,
  Col,
  DatePickerField,
  DateRangePickerField,
  Form,
  Row,
  SelectField,
  Space,
  SubmitButton,
  TextInputField,
} from '@/components';
import {
  CommentPublishTypeLabelModifier,
  CommentReadStatusLabelModifier,
  commentPublishType,
  commentReadStatusEnum,
} from '@/features/comment/models/comment';
import {
  CommentResolvedStatusLabelModifier,
  type CommentSearchFormSchema,
  commentResolvedStatusType,
} from '@/features/comment/models/search';
import { pinpointOptionsBySheet } from '@/features/comment/utils/pinpoint-options-by-sheet';
import { TagSelectField } from '@/features/tag/components';
import { tagType } from '@/features/tag/models';
import { useHierarchyLabels } from '@/features/tenant-manager/hierarchy-label/_hooks/hierarchy-label';
import { useOrganizationOptionsRecursive } from '@/features/tenant-manager/organization/_hooks/organization';
import { UserSelectField } from '@/features/tenant-manager/user/_components/UserSelectField';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { CommentListSearchFilterTags } from './CommentListSearchFilterTags';

const resolvedStatusOptions = commentResolvedStatusType.options.map(
  (value) => ({
    value,
    label: CommentResolvedStatusLabelModifier[value],
  }),
);
const readOptions = commentReadStatusEnum.options.map((value) => ({
  value,
  label: CommentReadStatusLabelModifier[value],
}));

const publishTypeOptions = commentPublishType.options.map((value) => ({
  label: CommentPublishTypeLabelModifier[value],
  value,
}));

type FormValues = z.infer<typeof CommentSearchFormSchema>;

type Props = {
  resetFormValues: FormValues;
  onSearch: (values: FormValues) => void;
};

export const CommentListSearchForm: FC<Props> = ({
  resetFormValues,
  onSearch,
}) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useFormContext<FormValues>();

  const { data: hierarchyLabelOptions } = useHierarchyLabels({
    params: { enabledOnly: true },
    select: (data) =>
      data.map((label) => ({
        value: label.id,
        label: label.name,
      })),
  });

  const organizationsOptions = useOrganizationOptionsRecursive();

  return (
    <Form
      leavePageAlertCondition={null}
      layout="vertical"
      onFinish={handleSubmit(onSearch)}
    >
      <Row gutter={[16, 0]}>
        <Col span={6}>
          <DateRangePickerField
            name="createdMonthRange"
            label={t('created-month')}
            control={control}
            errors={errors}
            picker="month"
            allowEmpty={[true, true]}
            fieldStyle={{ width: '100%' }}
          />
        </Col>
        <Col span={6}>
          <TagSelectField
            name="tagIds"
            control={control}
            errors={errors}
            type={tagType.Values.comment}
          />
        </Col>
        <Col span={6}>
          <TextInputField
            name="keyword"
            autocompleteKey="comment.keyword"
            label={t('keyword')}
            hint={t('comment.hint.search-keyword')}
            control={control}
            errors={errors}
          />
        </Col>
        <Col span={6}>
          <SelectField
            name="labelId"
            label={t('comment.label')}
            control={control}
            options={hierarchyLabelOptions}
            errors={errors}
            useNotInOption
          />
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col span={6}>
          <SelectField
            name="resolvedStatus"
            label={t('comment.resolved-status-label')}
            control={control}
            errors={errors}
            options={resolvedStatusOptions}
            allowClear={false}
          />
        </Col>
        <Col span={6}>
          <SelectField
            name="readStatus"
            label={t('comment.is-unread-label')}
            control={control}
            errors={errors}
            options={readOptions}
            allowClear={false}
          />
        </Col>
        <Col span={6}>
          <SelectField
            name="publishType"
            label={t('scope-of-disclosure')}
            control={control}
            options={publishTypeOptions}
            errors={errors}
            allowClear={false}
          />
        </Col>
        <Col span={6}>
          <CascaderField
            name="organizationIds"
            label={t('organization-in-charge')}
            control={control}
            options={organizationsOptions.data}
            multiple
            errors={errors}
          />
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col span={6}>
          <DatePickerField
            name="fiscalYear"
            label={t('created-fiscal-year')}
            control={control}
            errors={errors}
            picker="year"
            fieldStyle={{ width: '100%' }}
            shouldCoexistOnChange
          />
        </Col>
        <Col span={6}>
          <UserSelectField
            name="authorId"
            label={t('author')}
            control={control}
            errors={errors}
          />
        </Col>
        <Col span={6}>
          <UserSelectField
            name="assigneeId"
            label={t('comment.search-form-assignee')}
            control={control}
            errors={errors}
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <SelectField
            name="pinpointKeys"
            label={t('comment.target-name')}
            control={control}
            options={pinpointOptionsBySheet}
            errors={errors}
            tagMaxWidth={300}
            mode="multiple"
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 16 }}>
        <Col span={24}>
          <CommentListSearchFilterTags />
        </Col>
      </Row>
      <Space>
        <Button
          htmlType="button"
          type="default"
          onClick={() => {
            reset(resetFormValues);
          }}
        >
          {t('search-clear')}
        </Button>
        <SubmitButton text={t('search-by')} />
      </Space>
    </Form>
  );
};
